import { type DragEvent, useCallback } from 'react';

import { type Clip } from '@air/api/types';
import { CANVAS_BOARD_DROP_TYPE } from '@air/feature-edit-canvas';

import { setDragGrabAnchor } from '~/components/Shared/Drag/dragGrabAnchor';
import { type AssetDragItem, type BoardDragItem } from '~/components/Shared/Drag/dragTypes';
import { getCanvasAssetGrabAnchor } from '~/components/Shared/Drag/getCanvasAssetGrabAnchor';
import { useHTML5BoardGalleryDragExtensions } from '~/components/Shared/Drag/html5BoardGalleryDragExtensionsContext';
import { setCanvasAssetDragData } from '~/components/Shared/Drag/setCanvasAssetDragData';
import { isAssetDragItem } from '~/components/Shared/Drag/utils/isAssetDragItem';
import { isBoardDragItem } from '~/components/Shared/Drag/utils/isBoardDragItem';

export type UseHTML5DragStartParams = {
  item: BoardDragItem | AssetDragItem;
  isSelected: boolean;
  selectedAssets: Clip[];
  selectedBoardCount: number;
};

/**
 * Creates an onDragStart handler that sets HTML5 dataTransfer data for canvas/chat drop targets.
 * For boards, sets the board count so the canvas can show a rejection toast.
 * For assets, serializes asset payloads so the canvas can create image/video nodes on drop.
 */
export const useHTML5DragStart = ({
  item,
  isSelected,
  selectedAssets,
  selectedBoardCount,
}: UseHTML5DragStartParams) => {
  const { enrichDataTransfer, onDragEnd } = useHTML5BoardGalleryDragExtensions();

  const handleDragStart = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (!e.dataTransfer) {
        return;
      }

      const enrichParams = {
        dataTransfer: e.dataTransfer,
        isSelected,
        item,
        selectedAssets,
        selectedBoardCount,
      };

      if (isBoardDragItem(item)) {
        const boardCount = isSelected && selectedBoardCount > 0 ? selectedBoardCount : 1;
        e.dataTransfer.setData(CANVAS_BOARD_DROP_TYPE, String(boardCount));
        e.dataTransfer.effectAllowed = 'all';
        setDragGrabAnchor(0, 0);
        enrichDataTransfer(enrichParams);
        return;
      }

      if (!isAssetDragItem(item)) {
        return;
      }

      const assetsToSerialize = isSelected && selectedAssets.length > 0 ? selectedAssets : [item.asset];
      e.dataTransfer.setData('text/plain', item.asset.id);

      // setDragGrabAnchor(0,0) is intentional: the react-dnd drag layer uses a fixed cursor offset;
      // the grab anchor written to dataTransfer is for the canvas drop target only.
      setDragGrabAnchor(0, 0);
      setCanvasAssetDragData({
        dataTransfer: e.dataTransfer,
        clips: assetsToSerialize,
        grabAnchor: getCanvasAssetGrabAnchor(e, item.asset),
      });
      enrichDataTransfer(enrichParams);
    },
    [enrichDataTransfer, isSelected, item, selectedAssets, selectedBoardCount],
  );

  const handleDragEnd = useCallback(() => {
    onDragEnd();
  }, [onDragEnd]);

  return { handleDragEnd, handleDragStart };
};
